import React, { useState, useEffect, useMemo } from "react";
import {
  Switch,
  IconButton,
} from "@mui/material";
import axios from "axios";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorFooter from "../doctor_dashboard/DoctorFooter";
import SimpleSnackbar from "../Snackbar";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import {
  Menu,
  Star,
  UserRound,
  Stethoscope,
  CalendarDays,
  Eye,
  EyeOff,
  RefreshCw,
  MessageSquareText,
} from "lucide-react";

/* ================= LOADER ================= */
const PageLoader = () => {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
    </div>
  );
};

/* ================= HELPERS ================= */
const formatDate = (date) => {
  if (!date) return "—";

  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getInitials = (name = "") => {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((item) => item[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "PT"
  );
};

/* ================= REVIEW CARD ================= */
const ReviewCard = ({ review, toggleDisplay }) => {
  const patientName = review.patient?.name || "Unknown patient";
  const doctorName = review.doctor?.name || "N/A";

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm">
      {/* Top */}
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
          {getInitials(patientName)}
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-slate-950">
            {patientName}
          </h4>

          <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            <Stethoscope size={13} />
            <span className="truncate">Dr. {doctorName}</span>
          </p>

          <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
            <CalendarDays size={13} />
            <span>{formatDate(review.date)}</span>
          </p>
        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            review.display
              ? "bg-emerald-50 text-emerald-700"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {review.display ? "Visible" : "Hidden"}
        </span>
      </div>

      {/* Rating */}
      <div className="mt-5 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const active = Number(review.rating || 0) >= star;

          return (
            <Star
              key={star}
              size={18}
              className={
                active
                  ? "fill-amber-400 text-amber-400"
                  : "fill-slate-100 text-slate-200"
              }
            />
          );
        })}

        <span className="ml-2 text-sm font-medium text-slate-600">
          {review.rating || 0}/5
        </span>
      </div>

      {/* Comment */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Comment
        </p>

        <p className="mt-2 line-clamp-5 text-sm leading-6 text-slate-700">
          {review.comment || "No comment added."}
        </p>
      </div>

      {/* Display toggle */}
      <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="flex items-center gap-2">
          {review.display ? (
            <Eye size={16} className="text-slate-500" />
          ) : (
            <EyeOff size={16} className="text-slate-500" />
          )}

          <span className="text-sm font-medium text-slate-700">
            Show on website
          </span>
        </div>

        <Switch
          checked={!!review.display}
          onChange={() => toggleDisplay(review._id, review.display)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#075E54",
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#075E54",
            },
          }}
        />
      </div>
    </article>
  );
};

/* ================= MAIN ================= */
const AdminReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchReviews = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/reviews/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error(err);
      setSnackMessage("Failed to fetch reviews");
      setSnackColor("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const toggleDisplay = async (reviewId, currentValue) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/reviews/${reviewId}/display`,
        {
          display: !currentValue,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setReviews((prev) =>
        prev.map((review) =>
          review._id === reviewId
            ? { ...review, display: !currentValue }
            : review
        )
      );

      setSnackMessage(!currentValue ? "Review is now visible" : "Review hidden");
      setSnackColor("success");
    } catch (err) {
      console.error(err);
      setSnackMessage("Failed to update review display");
      setSnackColor("error");
    }
  };

  const stats = useMemo(() => {
    const total = reviews.length;
    const visible = reviews.filter((review) => review.display).length;
    const hidden = total - visible;

    const averageRating =
      total > 0
        ? (
            reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) /
            total
          ).toFixed(1)
        : "0.0";

    return {
      total,
      visible,
      hidden,
      averageRating,
    };
  }, [reviews]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">Reviews</h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Reviews" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Reviews"
          setActiveTab={() => setDrawerOpen(false)}
        />

        {/* Main */}
        <main className="relative flex-1 overflow-y-auto">
          {loading && <PageLoader />}

          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page Title */}
            <div className="mb-8 flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                    Reviews
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    Manage patient feedback displayed on the website.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={fetchReviews}
                  className="inline-flex h-11 w-fit items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                >
                  <RefreshCw size={16} />
                  Refresh
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Total reviews</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {stats.total.toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Visible</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {stats.visible.toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Hidden</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {stats.hidden.toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Average rating</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {stats.averageRating}
                  </p>
                </div>
              </div>
            </div>

            {/* Empty */}
            {!loading && reviews.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <MessageSquareText size={20} className="text-slate-500" />
                </div>

                <h3 className="text-base font-semibold text-slate-950">
                  No reviews yet
                </h3>

                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Submitted patient reviews will appear here.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-950">
                    All reviews
                  </h3>

                  <p className="text-sm text-slate-500">
                    {reviews.length.toLocaleString()} review
                    {reviews.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {reviews.map((review) => (
                    <ReviewCard
                      key={review._id}
                      review={review}
                      toggleDisplay={toggleDisplay}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        onClose={() => setSnackMessage("")}
      />
    </div>
  );
};

export default AdminReviewsPage;
